import React, { useState, useEffect, useRef, useContext } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import { Grid, makeStyles, Typography, Box } from "@material-ui/core";
import { green, red, orange } from "@material-ui/core/colors";
import { CheckCircle, Error as ErrorIcon, Sync } from "@material-ui/icons";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import UserStatusIcon from "../UserModal/statusIcon";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import ConnectionIcon from "../ConnectionIcon";

const useStyles = makeStyles((theme) => ({
  maxWidth: {
    width: "100%",
  },
  connectionOption: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    gap: theme.spacing(1),
  },
  connectionName: {
    flex: 1,
  },
  connectionStatus: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontSize: "0.875rem",
  },
  statusConnected: {
    color: green[600],
  },
  statusDisconnected: {
    color: red[600],
  },
  statusConnecting: {
    color: orange[600],
  },
}));

const filterOptions = createFilterOptions({
  trim: true,
});

const TransferTicketModalCustom = ({ modalOpen, onClose, ticketid, ticket, onSuccess }) => {
  const history = useHistory();
  const [options, setOptions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const classes = useStyles();
  const { findAll: findAllQueues } = useQueues();
  const isMounted = useRef(true);
  const [msgTransfer, setMsgTransfer] = useState('');
  const [companySettings, setCompanySettings] = useState({});
  const { user } = useContext(AuthContext);
  const { getAll: getAllSettings } = useCompanySettings();
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [loadingConnections, setLoadingConnections] = useState(false);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      const loadQueues = async () => {
        const list = await findAllQueues();
        setAllQueues(list);
        setQueues(list);

      };
      loadQueues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchCompanySettings = async () => {
      if (!user || !user.companyId) return;
      try {
        const settings = await getAllSettings(user.companyId);
        setCompanySettings(settings);
      } catch (err) {
        toastError(err);
      }
    };
    fetchCompanySettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ✅ NOVO: Carregar conexões disponíveis da empresa
  useEffect(() => {
    const fetchConnections = async () => {
      if (!user || !user.companyId || !modalOpen) return;
      setLoadingConnections(true);
      try {
        const { data } = await api.get("/whatsapp/");
        // Filtrar apenas conexões da empresa atual
        const companyConnections = data.filter(
          (conn) => conn.companyId === user.companyId
        );
        setConnections(companyConnections);
        
        // ✅ Inicializar com a conexão atual do ticket (se disponível)
        if (ticket?.whatsappId) {
          const currentConnection = companyConnections.find(
            (conn) => conn.id === ticket.whatsappId
          );
          if (currentConnection) {
            setSelectedConnection(currentConnection);
          } else {
            // Se a conexão atual não estiver na lista, limpar seleção (manter atual)
            setSelectedConnection(null);
          }
        } else {
          // Se não há conexão no ticket, limpar seleção
          setSelectedConnection(null);
        }
      } catch (err) {
        console.error("Erro ao carregar conexões:", err);
        toastError(err);
      } finally {
        setLoadingConnections(false);
      }
    };

    if (modalOpen) {
      fetchConnections();
    } else {
      // Limpar ao fechar o modal
      setSelectedConnection(null);
      setConnections([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, user, ticket?.whatsappId]);

  // ✅ NOVO: Quando as conexões forem carregadas e houver um usuário selecionado,
  // aplicar a conexão padrão do usuário
  useEffect(() => {
    if (selectedUser && selectedUser.whatsappId && connections.length > 0) {
      const userDefaultConnection = connections.find(
        (conn) => conn.id === selectedUser.whatsappId
      );
      if (userDefaultConnection) {
        setSelectedConnection(userDefaultConnection);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connections, selectedUser?.whatsappId]);


  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      setSelectedQueue("");
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/", {
            params: { searchParam, showAll: true },
          });
          setOptions(data.users);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };

      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const handleMsgTransferChange = (event) => {
    setMsgTransfer(event.target.value);
  };

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setSelectedUser(null);
    setSelectedConnection(null);
  };

  // ✅ NOVO: Função para obter o ícone e cor do status da conexão
  const getConnectionStatusDisplay = (status) => {
    const normalizedStatus = status?.toUpperCase() || "DISCONNECTED";
    
    if (normalizedStatus === "CONNECTED") {
      return {
        icon: <CheckCircle style={{ fontSize: 16, color: green[600] }} />,
        text: "CONNECTED",
        color: green[600],
      };
    } else if (["PAIRING", "OPENING", "QRCODE"].includes(normalizedStatus)) {
      return {
        icon: <Sync style={{ fontSize: 16, color: orange[600] }} />,
        text: normalizedStatus,
        color: orange[600],
      };
    } else {
      return {
        icon: <ErrorIcon style={{ fontSize: 16, color: red[600] }} />,
        text: "DISCONNECTED",
        color: red[600],
      };
    }
  };

  const handleSaveTicket = async (e) => {
    // e.preventDefault();
    if (!ticketid) return;
    if (!selectedQueue || selectedQueue === "") return;
    setLoading(true);
    try {
      let data = {};

      // ✅ CORRIGIDO: Em transferências manuais (isTransfered=true), SEMPRE passar o userId selecionado
      // O backend é responsável por decidir se aplica randomização ou não
      data.userId = !selectedUser ? null : selectedUser.id;
      
      // Define o status baseado na configuração de CompaniesSettings
      const transferStatus = companySettings.transferredTicketStatus || "open";
      data.status = !selectedUser ? "pending" : ticket.isGroup ? "group" : transferStatus;
      
      data.queueId = selectedQueue;
      data.msgTransfer = msgTransfer ? msgTransfer : null;
      data.isTransfered = true;
      
      // ✅ NOVO: Incluir whatsappId se uma conexão foi selecionada
      // Se não selecionar, mantém a conexão atual (não envia o campo)
      if (selectedConnection && selectedConnection.id) {
        data.whatsappId = selectedConnection.id;
      }

      await api.put(`/tickets/${ticketid}`, data);
      setLoading(false);
      if (typeof onSuccess === 'function') {
        onSuccess();
        handleClose();
      } else {
        history.push(`/tickets/`);
        handleClose();
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  return (
    <Dialog open={modalOpen} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
      {/* <form onSubmit={handleSaveTicket}> */}
      <DialogTitle id="form-dialog-title">
        {i18n.t("transferTicketModal.title")}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} xl={6}>
            <Autocomplete
              fullWidth
              getOptionLabel={(option) => `${option.name}`}
              onChange={(e, newValue) => {
                setSelectedUser(newValue);
                if (newValue != null && Array.isArray(newValue.queues)) {
                  if (newValue.queues.length === 1) {
                    setSelectedQueue(newValue.queues[0].id);
                  }
                  setQueues(newValue.queues);

                } else {
                  setQueues(allQueues);
                  setSelectedQueue("");
                }
                
                // ✅ NOVO: Quando um usuário é selecionado, buscar sua conexão padrão
                if (newValue && newValue.whatsappId) {
                  // Buscar a conexão padrão do usuário na lista de conexões disponíveis
                  const userDefaultConnection = connections.find(
                    (conn) => conn.id === newValue.whatsappId
                  );
                  if (userDefaultConnection) {
                    setSelectedConnection(userDefaultConnection);
                  } else {
                    // Se a conexão padrão do usuário não estiver na lista, limpar seleção
                    // (manterá a conexão atual do ticket)
                    setSelectedConnection(null);
                  }
                } else {
                  // Se o usuário não tem conexão padrão, limpar seleção
                  setSelectedConnection(null);
                }
              }}
              options={options}
              filterOptions={filterOptions}
              freeSolo
              autoHighlight
              noOptionsText={i18n.t("transferTicketModal.noOptions")}
              loading={loading}
              renderOption={option => (<span> <UserStatusIcon user={option} /> {option.name}</span>)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={i18n.t("transferTicketModal.fieldLabel")}
                  variant="outlined"
                  autoFocus
                  onChange={(e) => setSearchParam(e.target.value)}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid xs={12} sm={6} xl={6} item >
            <FormControl variant="outlined" fullWidth>
              <InputLabel>
                {i18n.t("transferTicketModal.fieldQueueLabel")}
              </InputLabel>
              <Select
                value={selectedQueue}
                onChange={(e) => setSelectedQueue(e.target.value)}
                label={i18n.t("transferTicketModal.fieldQueuePlaceholder")}
              >
                {[...queues]
                  .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))
                  .map((queue) => (
                    <MenuItem key={queue.id} value={queue.id}>
                      {queue.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} xl={12}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>
                {i18n.t("transferTicketModal.fieldConnectionLabel") || "Conexão de Destino"}
              </InputLabel>
              <Select
                value={selectedConnection?.id || ""}
                onChange={(e) => {
                  const connectionId = e.target.value;
                  const connection = connections.find((c) => c.id === connectionId);
                  setSelectedConnection(connection || null);
                }}
                label={i18n.t("transferTicketModal.fieldConnectionLabel") || "Conexão de Destino"}
                disabled={loadingConnections}
              >
                <MenuItem value="">
                  <em>
                    {i18n.t("transferTicketModal.fieldConnectionDefault") || 
                     "Manter conexão atual"}
                  </em>
                </MenuItem>
                {connections
                  .sort((a, b) => {
                    // Ordenar: CONNECTED primeiro, depois por nome
                    if (a.status === "CONNECTED" && b.status !== "CONNECTED") return -1;
                    if (a.status !== "CONNECTED" && b.status === "CONNECTED") return 1;
                    return (a.name || `Conexão ${a.id}`).localeCompare(
                      b.name || `Conexão ${b.id}`,
                      "pt-BR",
                      { sensitivity: "base" }
                    );
                  })
                  .map((connection) => {
                    const statusDisplay = getConnectionStatusDisplay(connection.status);
                    return (
                      <MenuItem key={connection.id} value={connection.id}>
                        <Box className={classes.connectionOption}>
                          <ConnectionIcon
                            connectionType={connection.channel || "whatsapp"}
                          />
                          <Typography className={classes.connectionName}>
                            {connection.name || `Conexão ${connection.id}`}
                          </Typography>
                          <Box
                            className={classes.connectionStatus}
                            style={{ color: statusDisplay.color }}
                          >
                            {statusDisplay.icon}
                            <Typography variant="caption">
                              {statusDisplay.text}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} xl={12} >
            <TextField
              label={i18n.t("transferTicketModal.msgTransfer")}
              value={msgTransfer}
              onChange={handleMsgTransferChange}
              variant="outlined"
              multiline
              maxRows={5}
              minRows={5}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="secondary"
          disabled={loading}
          variant="outlined"
        >
          {i18n.t("transferTicketModal.buttons.cancel")}
        </Button>
        <ButtonWithSpinner
          variant="contained"
          type="submit"
          color="primary"
          loading={loading}
          disabled={selectedQueue === ""}
          onClick={() => handleSaveTicket(selectedQueue)}

        >
          {i18n.t("transferTicketModal.buttons.ok")}
        </ButtonWithSpinner>
      </DialogActions>
      {/* </form> */}
    </Dialog>
  );
};

export default TransferTicketModalCustom;
